<template>
  <div
    ref="filters"
    class="filters"
  >
    <div :class="filtersLineClasses">
      <div class="wrap filters-line__content">
        <div
          ref="filtersBlock"
          class="filters-line__filters"
          :class="{ 'filters-line__filters-scroll': isOverlowFilterScroll }"
          @scroll="scrollHandler"
        >
          <slot name="filters"></slot>
        </div>

        <div class="filters-line__btn">
          <div
            v-if="isShowGradient"
            class="gradient"
          />
          <slot name="modal-button"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useResizeObserver } from '@vueuse/core';
  import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
  import { useScreenSize } from '@experience/shared';

  const { isMobile } = useScreenSize();

  const isScroll = ref(false);
  const filters = ref<null | HTMLElement>(null);
  const filtersBlock = ref<null | HTMLElement>(null);
  const isOverlowFilterScroll = ref(false);

  const isScrolledToEnd = ref(false);
  const hasOverflow = ref(false);

  const isShowGradient = computed(() => {
    return hasOverflow.value && !isScrolledToEnd.value;
  });

  const filtersLineClasses = computed(() => {
    return [
      'filters-line',
      {
        'filters-line_scroll': isScroll.value && !isMobile.value,
        'filters-line_scroll-mobile': isScroll.value && isMobile.value,
      },
    ];
  });

  const scrollHandler = () => {
    if (!filtersBlock.value) {
      return;
    }

    hasOverflow.value = filtersBlock.value.scrollWidth > filtersBlock.value.clientWidth;

    isScrolledToEnd.value =
      Math.abs(filtersBlock.value.scrollWidth - filtersBlock.value.clientWidth - filtersBlock.value.scrollLeft) < 2;
  };

  const updateFiltersPosition = () => {
    const rect = filters.value?.getBoundingClientRect();

    const hostOffset = filters.value
      ? getComputedStyle(filters.value)?.getPropertyValue('--host-sticky-offset')?.slice(0, 2)
      : '0';

    const top = rect ? rect.top - parseInt(hostOffset || '0') : 0;

    isScroll.value = Boolean(top < 0);
  };

  const setupResizeObserver = () => {
    if (!filtersBlock.value) {
      return;
    }

    useResizeObserver(filtersBlock.value, () => {
      scrollHandler(); // Обновляем состояние прокрутки при изменении размера
    });
  };

  const applyFilterOverflowScroll = () => {
    isOverlowFilterScroll.value = false;
    nextTick(() => {
      scrollHandler(); // Обновляем состояние прокрутки
    });
  };

  onMounted(() => {
    window.addEventListener('scroll', updateFiltersPosition);
    window.addEventListener('resize', applyFilterOverflowScroll);

    // Инициализируем состояние прокрутки после монтирования компонента
    nextTick(() => {
      setupResizeObserver();
      scrollHandler();
    });
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', updateFiltersPosition);
    window.removeEventListener('resize', applyFilterOverflowScroll);
  });

  defineExpose({
    isScroll,
    isOverlowFilterScroll,
    isShowGradient,
  });
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  .filters {
    position: relative;
    height: 64px;

    @include adaptivity.mobile {
      margin: 12px 0 0;
    }
  }

  .filters-line {
    background: var(--alias-color-background-default);

    :deep(.filter-popup-wrapper > :first-child) {
      transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
    }
  }

  .filters-line__content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    column-gap: 8px;

    @include adaptivity.mobile {
      flex-direction: column;
      padding: 0 20px;

      .filters-line_scroll-mobile & {
        padding-top: 8px;
      }

      .filters-line__filters {
        order: 1;
        padding: 8px 0;

        :deep(.filter-dates) {
          width: calc(50% - 4px);
        }

        :deep(.filter-tours-activities) {
          width: calc(50% - 4px);

          button {
            width: 100%;
            text-align: left;
          }
        }
      }

      :deep(.filters-line__btn) {
        width: 100%;

        .filter-popup-wrapper {
          width: 100%;
        }

        .filters-open-btn {
          width: 100%;

          button {
            width: 100%;
          }
        }
      }
    }
  }

  .filters-line__filters {
    display: flex;
    column-gap: 8px;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @include adaptivity.tablet {
      overflow-x: scroll;
      width: 100%;
      padding-top: 8px;
      padding-bottom: 8px;
      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    @include adaptivity.mobile {
      padding-right: 8px;
    }
  }

  .filters-line__filters-scroll {
    overflow-x: scroll;
  }

  .filters-line__btn {
    position: relative;

    :deep(.filters-open-btn .button) {
      transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
    }
  }

  .gradient {
    position: absolute;
    left: -58px;
    z-index: 999;
    width: 58px;
    height: 48px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, var(--alias-color-background-default) 100%);
    pointer-events: none;

    @include adaptivity.tablet {
      display: block;
    }

    @include adaptivity.mobile {
      display: none;
    }
  }

  .filters-line_scroll {
    position: fixed;
    top: var(--host-sticky-offset, 0);
    z-index: 999;
    width: 100%;
    border-bottom: 1px solid var(--alias-color-border-default);

    :deep(.skeleton-item),
    :deep(.filter-popup-wrapper > :first-child) {
      max-height: 32px;
    }

    :deep(.filter-popup-wrapper > :first-child) {
      @include typography.base-300-300-medium;
    }
  }

  .filters-line_scroll-mobile {
    position: fixed;
    top: var(--host-sticky-offset, 0);
    z-index: 999;
    width: 100%;
    border-bottom: 1px solid var(--alias-color-border-default);

    :deep(.filter-popup-wrapper > :first-child) {
      transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
    }
  }
</style>
